import { type DragEvent, type ReactNode, useCallback, useMemo, useState } from 'react'
import { cn } from '@util'
import { isDefined, isNotDefined } from '@terros/common'
import { Group, Stack } from '@mantine/core'
import { SizePrimitives } from '@front/model'
import { type TableColumnDef } from '../types'
import { MaterialSymbolIcon } from '../../terros/icon/MaterialSymbolIcon'

type Props<T extends TableColumnDef> = {
  columns: T[]
  renderColumn: (column: T) => ReactNode
  reorderable?: boolean
  selectedColumns?: string[]
  setSelectedColumns?: (columns: string[]) => void
}

export const SingleColumns = <T extends TableColumnDef>(props: Props<T>): ReactNode => {
  const { columns, renderColumn, reorderable, selectedColumns = [], setSelectedColumns } = props

  const [draggingIndex, setDraggingIndex] = useState<number | undefined>(undefined)
  const [dragToIndex, setDragToIndex] = useState<number | undefined>(undefined)

  const columnIds = useMemo(() => new Set(columns.map((c) => c.id)), [columns])

  const toggleableSelectedColumns = useMemo(
    () => selectedColumns.filter((id) => columnIds.has(id)),
    [selectedColumns, columnIds]
  )

  const sortedColumns = useMemo(() => {
    if (!reorderable) return columns

    const selectedSet = new Set(selectedColumns)
    const selectedInOrder = toggleableSelectedColumns.map((id) => columns.find((c) => c.id === id)).filter(isDefined)
    const unselected = columns.filter((c) => !selectedSet.has(c.id))

    if (isNotDefined(draggingIndex) || isNotDefined(dragToIndex)) {
      return [...selectedInOrder, ...unselected]
    }

    const reordered = [...selectedInOrder]
    const [dragged] = reordered.splice(draggingIndex, 1)
    if (dragged) {
      reordered.splice(dragToIndex, 0, dragged)
    }
    return [...reordered, ...unselected]
  }, [reorderable, columns, selectedColumns, toggleableSelectedColumns, draggingIndex, dragToIndex])

  const handleDragStart = useCallback((index: number): void => {
    setDraggingIndex(index)
  }, [])

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number): void => {
      e.preventDefault()
      if (isNotDefined(draggingIndex)) return
      if (index === dragToIndex) return
      if (index >= toggleableSelectedColumns.length) return
      setDragToIndex(index)
    },
    [draggingIndex, dragToIndex, toggleableSelectedColumns.length]
  )

  const handleDragEnd = useCallback((): void => {
    if (isNotDefined(draggingIndex) || isNotDefined(dragToIndex) || draggingIndex === dragToIndex) {
      setDraggingIndex(undefined)
      setDragToIndex(undefined)
      return
    }

    const reorderedToggleable = [...toggleableSelectedColumns]
    const [dragged] = reorderedToggleable.splice(draggingIndex, 1)
    if (dragged) {
      reorderedToggleable.splice(dragToIndex, 0, dragged)
    }

    const nonToggleable = selectedColumns.filter((id) => !columnIds.has(id))
    setSelectedColumns?.([...nonToggleable, ...reorderedToggleable])
    setDraggingIndex(undefined)
    setDragToIndex(undefined)
  }, [draggingIndex, dragToIndex, toggleableSelectedColumns, selectedColumns, columnIds, setSelectedColumns])

  if (!reorderable) return columns.map((column) => renderColumn(column))

  const selectedSet = new Set(selectedColumns)

  return (
    <Stack>
      {sortedColumns.map((column, index) => {
        const isSelected = selectedSet.has(column.id)
        const isDragging = draggingIndex === index
        const isDraggable = isSelected

        return (
          <Group
            key={column.id}
            className={cn({ 'opacity-50': isDragging, 'pl-8': !isDraggable })}
            draggable={isDraggable}
            wrap='nowrap'
            onDragEnd={isDraggable ? handleDragEnd : undefined}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragStart={isDraggable ? () => handleDragStart(index) : undefined}
            onDrop={isDraggable ? handleDragEnd : undefined}
          >
            {isDraggable && (
              <div onClick={(e) => e.stopPropagation()}>
                <MaterialSymbolIcon icon='drag_indicator' size={SizePrimitives[4]} className='cursor-grab' />
              </div>
            )}
            {renderColumn(column)}
          </Group>
        )
      })}
    </Stack>
  )
}
